import {
  EXPRESSION_TYPES,
  isBankruptcyCh13WorkoutPlan,
  isFlexModificationWorkoutPlan,
  isForbearanceWorkoutPlan,
  isPaymentDeferralWorkoutPlan,
  isRepaymentWorkoutPlan,
  PaymentDeferralWorkoutPlan,
  StepDefinitionId,
  Temporal,
  toLegacyDate,
  toTemporalInstant,
} from '@willow/types-iso';

import { getMostRecentQrpcEntrySince } from '../../../loan/qrpc';
import { getDaysPastDueAsOfDate as getDaysPastDueAsOfDateImpl } from '../../../utils/payments';
import { FunctionContext, LoanContext } from '../FunctionContext';
import { functionFrom, FunctionLibrary, getFunctionSignature, workflowFunctionFrom } from '../FunctionDefinition';

// A payment deferral is created in `offered` status and, once the borrower accepts, moves straight to
// `completed` (it bypasses `active`). A declined offer is `cancelled`.
//
// The deferral predicates below key off the *most recent* payment deferral rather than "any plan with
// this status". A loan can accumulate several deferrals over its life, so matching any historical plan
// would let a prior deferral prematurely satisfy a step for the current offer (e.g. an old completed
// deferral auto-completing the activate step before the current offer is activated).
const getMostRecentPaymentDeferral = (ctx: LoanContext) =>
  ctx
    .getLoanSnapshot()
    .configuration.workoutPlans.filter(isPaymentDeferralWorkoutPlan)
    .reduce<PaymentDeferralWorkoutPlan | undefined>(
      (mostRecent, wp) =>
        mostRecent == null || wp.createdAt.getTime() > mostRecent.createdAt.getTime() ? wp : mostRecent,
      undefined,
    );

const loanHasPaymentDeferral = functionFrom(
  'loanHasPaymentDeferral',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) => getMostRecentPaymentDeferral(ctx)?.status === 'completed',
  "Returns true if the loan's most recent payment deferral is accepted and in effect (a payment-deferral workout plan in completed status).",
);

const loanHasOfferedPaymentDeferral = functionFrom(
  'loanHasOfferedPaymentDeferral',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) =>
    ctx
      .getLoanSnapshot()
      .configuration.workoutPlans.some((wp) => isPaymentDeferralWorkoutPlan(wp) && wp.status === 'offered'),
  'Returns true if the loan has a payment deferral offer that has not yet been accepted or declined (offered status).',
);

const loanHasCancelledPaymentDeferral = functionFrom(
  'loanHasCancelledPaymentDeferral',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) => getMostRecentPaymentDeferral(ctx)?.status === 'cancelled',
  "Returns true if the loan's most recent payment deferral is a declined offer (a payment-deferral workout plan in cancelled status).",
);

const loanHasRepaymentPlan = functionFrom(
  'loanHasRepaymentPlan',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) =>
    ctx.getLoanSnapshot().configuration.workoutPlans.some((wp) => isRepaymentWorkoutPlan(wp) && wp.status === 'active'),
  'Returns true if the loan has an active repayment workout plan.',
);

const loanHasBankruptcyPlan = functionFrom(
  'loanHasBankruptcyPlan',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) =>
    ctx
      .getLoanSnapshot()
      .configuration.workoutPlans.some((wp) => isBankruptcyCh13WorkoutPlan(wp) && wp.status === 'active'),
  'Returns true if the loan has an active Chapter 13 bankruptcy workout plan.',
);

const loanHasForebearancePlan = functionFrom(
  'loanHasForebearancePlan',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) =>
    ctx
      .getLoanSnapshot()
      .configuration.workoutPlans.some((wp) => isForbearanceWorkoutPlan(wp) && wp.status === 'active'),
  'Returns true if the loan has an active forbearance workout plan.',
);

// A loan modification is a flex modification with no trial period. Both are stored as flexModification
// workout plans, so this covers either. It matches an active plan (just created) or a completed one (the
// modification has been applied) so the step stays complete through the create-then-complete flow.
const loanHasFlexModificationPlan = functionFrom(
  'loanHasFlexModificationPlan',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) =>
    ctx
      .getLoanSnapshot()
      .configuration.workoutPlans.some(
        (wp) => isFlexModificationWorkoutPlan(wp) && (wp.status === 'active' || wp.status === 'completed'),
      ),
  'Returns true if the loan has an active or completed flex modification (or loan modification) workout plan.',
);

const loanIsInActiveForeclosure = functionFrom(
  'loanIsInActiveForeclosure',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) => ctx.getLoanSnapshot().configuration.foreclosures.some((f) => f.status === 'active'),
  'Returns true if the loan has an active foreclosure.',
);

const loanHasAppraisalOrderedSince = functionFrom(
  'loanHasAppraisalOrderedSince',
  [{ name: 'sinceDate', type: EXPRESSION_TYPES.zonedDateTime }] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, sinceZdt) => {
    const sinceInstant = sinceZdt.toInstant();
    return (ctx.getLoanSnapshot().configuration.reportableEvents ?? []).some(
      (event) =>
        event.type === 'appraisalOrdered' &&
        Temporal.Instant.compare(toTemporalInstant(event.updatedAt), sinceInstant) >= 0,
    );
  },
  'Returns true if an appraisal has been ordered on the loan since the given date time.',
);

const loanHasInspectionOrderedSince = functionFrom(
  'loanHasInspectionOrderedSince',
  [{ name: 'sinceDate', type: EXPRESSION_TYPES.zonedDateTime }] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, sinceZdt) => {
    const sinceInstant = sinceZdt.toInstant();
    return (ctx.getLoanSnapshot().configuration.reportableEvents ?? []).some(
      (event) =>
        event.type === 'inspectionOrdered' &&
        Temporal.Instant.compare(toTemporalInstant(event.updatedAt), sinceInstant) >= 0,
    );
  },
  'Returns true if a property inspection has been ordered on the loan since the given date time.',
);

const loanHasInspectionRecordedSince = functionFrom(
  'loanHasInspectionRecordedSince',
  [{ name: 'sinceDate', type: EXPRESSION_TYPES.zonedDateTime }] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, sinceZdt) => {
    const sinceInstant = sinceZdt.toInstant();
    return ctx
      .getLoanSnapshot()
      .configuration.inspections.some(
        (inspection) => Temporal.Instant.compare(toTemporalInstant(inspection.createdAt), sinceInstant) >= 0,
      );
  },
  'Returns true if a property inspection has been recorded (obtained) on the loan since the given date time.',
);

const loanHasActiveFlexModification = functionFrom(
  'loanHasActiveFlexModification',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) =>
    ctx
      .getLoanSnapshot()
      .configuration.workoutPlans.some((plan) => plan.type === 'flexModification' && plan.status === 'active'),
  'Returns true if the loan has an active flex modification trial period plan.',
);

const loanHasResolvedFlexModification = functionFrom(
  'loanHasResolvedFlexModification',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) =>
    ctx
      .getLoanSnapshot()
      .configuration.workoutPlans.some(
        (plan) => plan.type === 'flexModification' && (plan.status === 'completed' || plan.status === 'cancelled'),
      ),
  'Returns true if the loan has a flex modification workout plan that has been resolved — either converted to a permanent modification (completed) or closed out (cancelled).',
);

const loanHasNoPastDueStatements = functionFrom(
  'loanHasNoPastDueStatements',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (_ctx) => false, // TODO
  'Returns true when no past-due billing statements exist for the loan (stub implementation).',
);

const loanIsREO = functionFrom(
  'loanIsREO',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) => ctx.getLoanSnapshot().configuration.loanStatus === 'reo',
  'Returns true if the loan is currently in REO (real-estate-owned) status.',
);

const loanHasQRPCObtainedSince = functionFrom(
  'loanHasQRPCObtainedSince',
  [{ name: 'sinceDate', type: EXPRESSION_TYPES.zonedDateTime }] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, sinceZdt) => {
    const mostRecentQrpcEntry = getMostRecentQrpcEntrySince(
      ctx.getLoanSnapshot().configuration.qrpc ?? [],
      sinceZdt.toPlainDate(),
    );
    return mostRecentQrpcEntry?.qrpcObtainedFlag ?? false;
  },
  "Returns true if the loan's most recent QRPC entry since the given date time indicates that QRPC was obtained.",
);

const loanHasCallLoggedSince = workflowFunctionFrom(
  'loanHasCallLoggedSince',
  [{ name: 'sinceDate', type: EXPRESSION_TYPES.zonedDateTime }] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, sinceZdt) => {
    const mostRecentCallLoggedAt = ctx.getMostRecentCallLoggedAt();
    if (mostRecentCallLoggedAt == null) {
      return false;
    }
    return Temporal.Instant.compare(toTemporalInstant(mostRecentCallLoggedAt), sinceZdt.toInstant()) >= 0;
  },
  'Returns true if a call has been logged on the loan since the given date time. A logged call is a loan note whose contact type is one of the "Log Call" outcomes (inbound/outbound call, voicemail, no contact, other) — independent of whether QRPC was obtained. A no-contact call counts even though it writes no QRPC entry.',
);

/**
 * Returns the next QRPC collection due date:
 * - If the loan has a QRPC promise to pay expiration date, retry/due on the promise to pay expiration date
 * - If the loan does not have a QRPC promise to pay expiration date, retry/due 6 days from the last attempt
 */
const getNextQrpcDueDateTime = workflowFunctionFrom(
  'getNextQrpcDueDateTime',
  [
    { name: 'since', type: EXPRESSION_TYPES.zonedDateTime },
    { name: 'stepDefinitionId', type: EXPRESSION_TYPES.string },
  ] as const,
  EXPRESSION_TYPES.zonedDateTime,
  (ctx, sinceZdt, stepDefinitionId) => {
    const mostRecentQrpcEntry = getMostRecentQrpcEntrySince(
      ctx.getLoanSnapshot().configuration.qrpc ?? [],
      sinceZdt.toPlainDate(),
    );
    if (mostRecentQrpcEntry?.promiseToPayExpirationDate != null) {
      return toTemporalInstant(mostRecentQrpcEntry.promiseToPayExpirationDate).toZonedDateTimeISO(ctx.timeZone);
    }
    const lastStepAttempt = [...(ctx.getStepByDefinitionId(stepDefinitionId as StepDefinitionId)?.statusHistory ?? [])]
      .reverse()
      .find((status) => status.status === 'active');
    if (lastStepAttempt == null) {
      // this should never happen - steps should always have at least one history entry
      throw new Error(`Step with definitionId ${stepDefinitionId} has no activation history`);
    }
    return lastStepAttempt.timestamp.add(Temporal.Duration.from({ days: 6 }));
  },
  'Returns the zoned date-time of when the next QRPC collection attempt should be made.',
);

const loanHasEscrowAnalysisDraft = workflowFunctionFrom(
  'loanHasEscrowAnalysisDraft',
  [] as const,
  EXPRESSION_TYPES.boolean,
  (ctx) => ctx.hasEscrowAnalysisDraft(),
  'Returns true if the loan currently has a draft (not-yet-shared) escrow analysis.',
);

const loanHasAppraisalWithId = functionFrom(
  'loanHasAppraisalWithId',
  [{ name: 'appraisalId', type: EXPRESSION_TYPES.string }] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, appraisalId) => ctx.getLoanSnapshot().configuration.appraisals.some((a) => a.id === appraisalId),
  'Returns true if the loan snapshot has an appraisal with the given ID. Pair with `preallocatedEntityRef` for workflow steps that record an appraisal.',
);

const loanHasInspectionWithId = functionFrom(
  'loanHasInspectionWithId',
  [{ name: 'inspectionId', type: EXPRESSION_TYPES.string }] as const,
  EXPRESSION_TYPES.boolean,
  (ctx, inspectionId) => ctx.getLoanSnapshot().configuration.inspections.some((i) => i.id === inspectionId),
  'Returns true if the loan snapshot has an inspection with the given ID. Pair with `preallocatedEntityRef` for workflow steps that record an inspection.',
);

const loanStatus = functionFrom(
  'loanStatus',
  [] as const,
  EXPRESSION_TYPES.string,
  (ctx) => ctx.getLoanSnapshot().configuration.loanStatus,
  'Returns the current loan status (e.g. "active", "pendingTransfer", "reo", "paidOff").',
);

const transferServicerName = functionFrom(
  'transferServicerName',
  [] as const,
  EXPRESSION_TYPES.string,
  (ctx) => ctx.getLoanSnapshot().configuration.transferData?.servicerName ?? '',
  'Returns the name of the servicer the loan is being transferred to, or an empty string if the loan is not being transferred.',
);

const poolId = functionFrom(
  'poolId',
  [] as const,
  EXPRESSION_TYPES.string,
  (ctx) => ctx.getLoan().poolId ?? '',
  'Returns the pool ID the loan belongs to, or an empty string if the loan has no pool ID.',
);

const escrowBalance = functionFrom(
  'escrowBalance',
  [] as const,
  EXPRESSION_TYPES.number,
  (ctx) => ctx.getLoanSnapshot().configuration.snapshot.balances.escrowBalance,
  "Returns the loan's current escrow balance. A negative value means the borrower owes escrow.",
);

const getNumDaysPastDueAsOfDate = functionFrom(
  'getNumDaysPastDueAsOfDate',
  [{ name: 'targetDate', type: EXPRESSION_TYPES.zonedDateTime }] as const,
  EXPRESSION_TYPES.number,
  (ctx, targetDate) =>
    getDaysPastDueAsOfDateImpl(ctx.getLoanSnapshot().configuration, toLegacyDate(targetDate), ctx.timeZone.id),
  'Returns the number of days past due for the loan as of the given date.',
);

// Loan functions that only read the loan + snapshot — safe in any evaluator (workflow or email).
export const LOAN_FUNCTIONS = {
  [getFunctionSignature(loanHasPaymentDeferral)]: loanHasPaymentDeferral,
  [getFunctionSignature(loanHasOfferedPaymentDeferral)]: loanHasOfferedPaymentDeferral,
  [getFunctionSignature(loanHasCancelledPaymentDeferral)]: loanHasCancelledPaymentDeferral,
  [getFunctionSignature(loanHasRepaymentPlan)]: loanHasRepaymentPlan,
  [getFunctionSignature(loanHasBankruptcyPlan)]: loanHasBankruptcyPlan,
  [getFunctionSignature(loanHasForebearancePlan)]: loanHasForebearancePlan,
  [getFunctionSignature(loanHasFlexModificationPlan)]: loanHasFlexModificationPlan,
  [getFunctionSignature(loanIsInActiveForeclosure)]: loanIsInActiveForeclosure,
  [getFunctionSignature(loanHasAppraisalOrderedSince)]: loanHasAppraisalOrderedSince,
  [getFunctionSignature(loanHasInspectionOrderedSince)]: loanHasInspectionOrderedSince,
  [getFunctionSignature(loanHasInspectionRecordedSince)]: loanHasInspectionRecordedSince,
  [getFunctionSignature(loanHasActiveFlexModification)]: loanHasActiveFlexModification,
  [getFunctionSignature(loanHasResolvedFlexModification)]: loanHasResolvedFlexModification,
  [getFunctionSignature(loanHasNoPastDueStatements)]: loanHasNoPastDueStatements,
  [getFunctionSignature(loanIsREO)]: loanIsREO,
  [getFunctionSignature(loanStatus)]: loanStatus,
  [getFunctionSignature(transferServicerName)]: transferServicerName,
  [getFunctionSignature(poolId)]: poolId,
  [getFunctionSignature(escrowBalance)]: escrowBalance,
  [getFunctionSignature(loanHasQRPCObtainedSince)]: loanHasQRPCObtainedSince,
  [getFunctionSignature(getNumDaysPastDueAsOfDate)]: getNumDaysPastDueAsOfDate,
  [getFunctionSignature(loanHasAppraisalWithId)]: loanHasAppraisalWithId,
  [getFunctionSignature(loanHasInspectionWithId)]: loanHasInspectionWithId,
} satisfies FunctionLibrary;

// Loan functions that also depend on workflow step state (e.g. last step activation) — workflow-only.
export const LOAN_WORKFLOW_FUNCTIONS = {
  [getFunctionSignature(getNextQrpcDueDateTime)]: getNextQrpcDueDateTime,
  [getFunctionSignature(loanHasEscrowAnalysisDraft)]: loanHasEscrowAnalysisDraft,
  [getFunctionSignature(loanHasCallLoggedSince)]: loanHasCallLoggedSince,
} satisfies FunctionLibrary<FunctionContext>;
